// src/components/PaiementNitaAmanaModal.js
// Modal de paiement réel NITA/AMANA : collecte le moyen + le téléphone,
// initie la demande côté passerelle, puis interroge périodiquement le statut
// jusqu'à confirmation (ou échec/expiration). Remplace toute logique de
// paiement "simulé" — aucun abonnement/quota n'est jamais crédité tant que
// cette modal n'a pas reçu un statut réellement payé du serveur.
import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import QRCode from "qrcode";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost/securecontrol-backend";

const COLORS = {
  primary: '#0B3C5D',
  secondary: '#1E88C8',
  darkText: '#2E2E2E',
  lightBg: '#F5F7FA',
  danger: '#f44336',
  success: '#4caf50',
};

const POLL_INTERVAL_MS = 4000;
const MAX_POLL_ATTEMPTS = 90; // 6 minutes environ

// Nita : 1=payé, 2=annulé, 3=bloqué, 4=expiré (voir migrations/2026_07_10_passerelles.sql)
const NITA_TERMINAL = { 1: 'success', 2: 'failed', 3: 'failed', 4: 'failed' };
const AMANA_TERMINAL = { SUCCESS: 'success', FAILED: 'failed', EXPIRED: 'failed' };

const TOUS_LES_MOYENS = [
  { value: 'nita', label: 'NITA', logo: '/assets/img/mynita_logo.png' },
  { value: 'amana', label: 'Amana', logo: '/assets/img/amana_logo.png' },
];

const PaiementNitaAmanaModal = ({ show, idPaiement, montant, onClose, onSuccess }) => {
  const [etape, setEtape] = useState('choix'); // choix | initiation | attente | succes | echec
  const [gateway, setGateway] = useState('nita');
  const [phone, setPhone] = useState('');
  const [erreur, setErreur] = useState('');
  const [codeAffiche, setCodeAffiche] = useState(null);
  const [qrDataUrl, setQrDataUrl] = useState(null);
  const [moyensActifs, setMoyensActifs] = useState(null); // null = pas encore chargé
  const pollCountRef = useRef(0);
  const pollTimeoutRef = useRef(null);

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("access_token")}` });

  const stopPolling = () => {
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
  };

  useEffect(() => {
    if (show) {
      setEtape('choix');
      setPhone('');
      setErreur('');
      setCodeAffiche(null);
      setQrDataUrl(null);
      pollCountRef.current = 0;

      // Une passerelle désactivée par l'admin (Paramètres > Paiement) ne doit
      // pas être proposée comme option ici, même si elle reste techniquement
      // implémentée côté backend.
      setMoyensActifs(null);
      axios.get(`${API_BASE_URL}/paiements/gateway_public_list.php`, { headers: authHeaders() })
        .then(res => {
          const codes = (res.data?.passerelles || []).map(p => p.code);
          setMoyensActifs(codes);
          setGateway(prev => (codes.includes(prev) ? prev : (codes[0] || '')));
        })
        .catch(() => {
          // En cas d'erreur réseau, on retombe sur les deux moyens historiques
          // plutôt que de bloquer complètement le paiement.
          setMoyensActifs(['nita', 'amana']);
        });
    } else {
      stopPolling();
    }
    return stopPolling;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, idPaiement]);

  const poll = useCallback(async (refValue) => {
    pollCountRef.current += 1;
    if (pollCountRef.current > MAX_POLL_ATTEMPTS) {
      setEtape('echec');
      setErreur("Le paiement n'a pas été confirmé dans le délai imparti. Réessayez ou contactez le support si le montant a été débité.");
      return;
    }

    try {
      const url = gateway === 'nita'
        ? `${API_BASE_URL}/paiements/nita_check_status.php?request_id=${encodeURIComponent(refValue)}`
        : `${API_BASE_URL}/paiements/amana_check_status.php?external_reference=${encodeURIComponent(refValue)}`;
      const res = await axios.get(url, { headers: authHeaders() });

      if (res.data?.success) {
        const statut = res.data.statut;
        const table = gateway === 'nita' ? NITA_TERMINAL : AMANA_TERMINAL;
        const issue = table[statut];

        if (issue === 'success') {
          setEtape('succes');
          setTimeout(() => onSuccess?.(), 1200);
          return;
        }
        if (issue === 'failed') {
          setEtape('echec');
          setErreur(res.data.message || "Le paiement a échoué, a été annulé ou a expiré.");
          return;
        }
      }
    } catch (err) {
      // Erreur réseau ponctuelle : on continue le polling plutôt que d'échouer tout de suite.
    }

    pollTimeoutRef.current = setTimeout(() => poll(refValue), POLL_INTERVAL_MS);
  }, [gateway, onSuccess]);

  const handleInitier = async (e) => {
    e.preventDefault();
    setErreur('');

    const digits = phone.replace(/\D/g, '');
    if (digits.length < 8) {
      setErreur('Numéro de téléphone requis (8 chiffres minimum).');
      return;
    }

    const nomGateway = gateway === 'nita' ? 'NITA' : 'Amana';
    const montantAffiche = montant ? `${new Intl.NumberFormat('fr-FR').format(montant)} FCFA` : 'ce montant';
    const confirme = window.confirm(
      `Êtes-vous sûr de vouloir lancer le paiement de ${montantAffiche} via ${nomGateway} au numéro +227 ${digits} ?`
    );
    if (!confirme) {
      return;
    }

    setEtape('initiation');
    try {
      const url = gateway === 'nita'
        ? `${API_BASE_URL}/paiements/nita_initier.php`
        : `${API_BASE_URL}/paiements/amana_initier.php`;
      const res = await axios.post(url, { id_paiement: idPaiement, phone: digits }, {
        headers: { ...authHeaders(), 'Content-Type': 'application/json' }
      });

      if (res.data?.success) {
        // "reference" sert au polling de statut (request_id NITA / external_reference
        // Amana) ; "codeAffiche" est le code que le client utilise réellement pour
        // payer (code_achat NITA au guichet/MyNITA — le request_id n'a aucun sens
        // pour lui). Amana n'a pas de code distinct, on réaffiche sa référence.
        const ref = gateway === 'nita' ? res.data.request_id : res.data.external_reference;
        const code = gateway === 'nita' ? (res.data.code_achat || ref) : ref;
        setCodeAffiche(code);
        QRCode.toDataURL(code, { width: 180, margin: 1, color: { dark: '#0B3C5D', light: '#FFFFFF' } })
          .then(setQrDataUrl)
          .catch(() => setQrDataUrl(null));
        setEtape('attente');
        pollCountRef.current = 0;
        poll(ref);
      } else {
        setEtape('choix');
        setErreur(res.data?.message || "Impossible d'initier le paiement.");
      }
    } catch (err) {
      setEtape('choix');
      setErreur(err.response?.data?.message || "Impossible d'initier le paiement.");
    }
  };

  if (!show) return null;

  return (
    <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content" style={{ borderRadius: '12px' }}>
          <div className="modal-header">
            <h5 className="modal-title" style={{ color: COLORS.primary, fontWeight: '600' }}>
              <i className="bi bi-credit-card me-2"></i>
              Paiement — {montant ? `${new Intl.NumberFormat('fr-FR').format(montant)} FCFA` : ''}
            </h5>
            {(etape === 'choix') && (
              <button type="button" className="btn-close" onClick={onClose}></button>
            )}
          </div>
          <div className="modal-body">
            {erreur && etape !== 'echec' && (
              <div className="alert alert-danger">{erreur}</div>
            )}

            {etape === 'choix' && moyensActifs === null && (
              <div className="text-center py-4">
                <div className="spinner-border spinner-border-sm" style={{ color: COLORS.primary }} role="status"></div>
                <p className="mt-2 mb-0" style={{ color: COLORS.darkText, fontSize: '0.9rem' }}>Chargement des moyens de paiement...</p>
              </div>
            )}

            {etape === 'choix' && moyensActifs !== null && moyensActifs.length === 0 && (
              <div className="alert alert-warning mb-0">
                Aucun moyen de paiement n'est actuellement disponible. Veuillez contacter l'administrateur.
              </div>
            )}

            {etape === 'choix' && moyensActifs !== null && moyensActifs.length > 0 && (
              <form onSubmit={handleInitier}>
                <label className="form-label fw-semibold" style={{ color: COLORS.darkText }}>Moyen de paiement</label>
                <div className="row g-3 mb-3">
                  {TOUS_LES_MOYENS.filter(g => moyensActifs.includes(g.value)).map(g => (
                    <div className="col-6" key={g.value}>
                      <div
                        className="card h-100 text-center"
                        style={{
                          cursor: 'pointer',
                          border: gateway === g.value ? `2px solid ${COLORS.primary}` : '1px solid #e0e0e0',
                          backgroundColor: gateway === g.value ? '#E3F2FD' : 'white',
                          borderRadius: '10px'
                        }}
                        onClick={() => setGateway(g.value)}
                      >
                        <div className="card-body py-3">
                          <img src={g.logo} alt={g.label} style={{ height: '40px', objectFit: 'contain' }} />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <label className="form-label fw-semibold" style={{ color: COLORS.darkText }}>
                  Numéro de téléphone (paiement mobile) <span style={{ color: COLORS.danger }}>*</span>
                </label>
                <div className="input-group mb-1">
                  <span className="input-group-text">🇳🇪 +227</span>
                  <input
                    type="tel"
                    className="form-control"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 8))}
                    placeholder="Ex: 98337777"
                    maxLength={8}
                    required
                  />
                </div>
                <div className="form-text mb-3" style={{ fontSize: '0.78rem' }}>
                  8 chiffres — l'indicatif +227 est ajouté automatiquement.
                </div>

                <button type="submit" className="btn w-100 fw-semibold"
                  style={{ backgroundColor: COLORS.primary, borderColor: COLORS.primary, color: 'white', borderRadius: '8px', padding: '10px' }}>
                  <i className="bi bi-arrow-right-circle me-2"></i>
                  Lancer le paiement
                </button>
              </form>
            )}

            {etape === 'initiation' && (
              <div className="text-center py-4">
                <div className="spinner-border" style={{ color: COLORS.primary }} role="status"></div>
                <p className="mt-3" style={{ color: COLORS.darkText }}>Initiation du paiement...</p>
              </div>
            )}

            {etape === 'attente' && (
              <div className="text-center py-4">
                <div className="spinner-border" style={{ color: COLORS.secondary }} role="status"></div>
                <h6 className="mt-3" style={{ color: COLORS.primary, fontWeight: '600' }}>
                  En attente de confirmation
                </h6>
                <p style={{ color: COLORS.darkText, fontSize: '0.9rem' }}>
                  {gateway === 'nita'
                    ? <>Composez le code reçu ou payez via l'application <strong>MyNITA</strong> pour confirmer.</>
                    : <>Confirmez le paiement depuis l'application <strong>AmanaTa</strong> sur votre téléphone.</>}
                </p>
                {codeAffiche && (
                  <div className="mt-3 p-3 rounded d-flex flex-column align-items-center" style={{ backgroundColor: COLORS.lightBg }}>
                    {qrDataUrl && (
                      <img src={qrDataUrl} alt="QR code du code d'achat" className="mb-2" style={{ width: '140px', height: '140px' }} />
                    )}
                    <div style={{ fontSize: '0.78rem', color: '#757575' }}>
                      {gateway === 'nita' ? "Code d'achat" : 'Référence de paiement'}
                    </div>
                    <div style={{
                      fontSize: '1.15rem',
                      fontWeight: '700',
                      letterSpacing: '0.06em',
                      color: COLORS.primary,
                      fontFamily: 'monospace',
                      wordBreak: 'break-all'
                    }}>
                      {codeAffiche}
                    </div>
                  </div>
                )}
                <p className="mt-3 mb-0" style={{ fontSize: '0.8rem', color: '#757575' }}>
                  Cette page se met à jour automatiquement. Vous pouvez fermer cette fenêtre et
                  revenir plus tard : le paiement reste en attente et pourra être finalisé depuis
                  la liste des abonnements (valable 24h).
                </p>
              </div>
            )}

            {etape === 'succes' && (
              <div className="text-center py-4">
                <div className="rounded-circle mx-auto mb-3 d-flex align-items-center justify-content-center"
                  style={{ width: '70px', height: '70px', backgroundColor: '#e8f5e9' }}>
                  <i className="bi bi-check-lg" style={{ fontSize: '2rem', color: COLORS.success }}></i>
                </div>
                <h6 style={{ color: COLORS.success, fontWeight: '600' }}>Paiement confirmé !</h6>
              </div>
            )}

            {etape === 'echec' && (
              <div className="text-center py-4">
                <div className="rounded-circle mx-auto mb-3 d-flex align-items-center justify-content-center"
                  style={{ width: '70px', height: '70px', backgroundColor: '#ffebee' }}>
                  <i className="bi bi-x-lg" style={{ fontSize: '2rem', color: COLORS.danger }}></i>
                </div>
                <h6 style={{ color: COLORS.danger, fontWeight: '600' }}>Paiement non confirmé</h6>
                <p style={{ color: COLORS.darkText, fontSize: '0.9rem' }}>{erreur}</p>
                <button className="btn fw-semibold mt-2" onClick={() => { setEtape('choix'); setErreur(''); }}
                  style={{ borderColor: COLORS.primary, color: COLORS.primary, borderRadius: '8px' }}>
                  <i className="bi bi-arrow-clockwise me-2"></i>
                  Réessayer
                </button>
              </div>
            )}
          </div>
          {(etape === 'choix' || etape === 'echec') && (
            <div className="modal-footer">
              <button type="button" className="btn" onClick={onClose}>Annuler</button>
            </div>
          )}
          {etape === 'attente' && (
            <div className="modal-footer">
              <button type="button" className="btn" onClick={onClose}>
                <i className="bi bi-x-lg me-1"></i> Fermer
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaiementNitaAmanaModal;
